import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { pathIn, urlLanguage, withoutLanguage } from '../content/publicLanguages';

import { LANGUAGE_CODES as SUPPORTED_LANGUAGES, LANGUAGES } from '../locales';
const LANGUAGE_LABELS = Object.fromEntries(
  LANGUAGES.map((l) => [l.code, l.code.toUpperCase()])
);

const PublicLanguageSelect = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);
  const currentLanguage = SUPPORTED_LANGUAGES.includes(i18n.resolvedLanguage)
    ? i18n.resolvedLanguage
    : 'fr';

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // The page in the chosen language has its own URL: go there (or back to the English URL when this
  // page has no version in that language), so the address always matches what is read.
  const handleLanguageChange = (language) => {
    i18n.changeLanguage(language);
    setIsOpen(false);
    const target = pathIn(location.pathname, language)
      || (urlLanguage(location.pathname) ? withoutLanguage(location.pathname) : null);
    if (target && target !== location.pathname) navigate(`${target}${location.search}${location.hash}`);
  };

  return (
    <div className="public-language-dropdown" ref={menuRef}>
      <button
        type="button"
        className={`public-language-trigger${isOpen ? ' is-open' : ''}`}
        aria-label={t('public.language.aria')}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((value) => !value)}
      >
        <span>{LANGUAGE_LABELS[currentLanguage]}</span>
        <span className="public-language-trigger-icon" aria-hidden="true">
          ▼
        </span>
      </button>

      {isOpen && (
        <div className="public-language-menu" role="menu" aria-label={t('public.language.aria')}>
          {SUPPORTED_LANGUAGES.map((language) => (
            <button
              key={language}
              type="button"
              role="menuitemradio"
              aria-checked={currentLanguage === language}
              className={`public-language-option${currentLanguage === language ? ' is-active' : ''}`}
              onClick={() => handleLanguageChange(language)}
            >
              {LANGUAGE_LABELS[language]}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default PublicLanguageSelect;
